/* eslint-disable react/prop-types */
/**
 * The card that opens over the map once a property has been pinned.
 *
 * Replaces the crowd-data request form for this flow. That form asks for state,
 * property type and a page of record identifiers — everything the pin already
 * answers — so here the card only asks the one thing the map cannot: WHICH
 * document. Three compact steps in place of a multi-section modal:
 *
 *   address  →  pick a document  →  pick a delivery option  →  request
 *
 * The delivery step is not decoration: without an explicit service the backend
 * falls back to a flat default price (see crowddatarequest/pricing.js), which
 * would overcharge a ₹49 soft copy and undercharge a ₹1599 hard copy. Showing
 * the real options is what keeps the charge honest.
 *
 * The request it creates is an ordinary document order — it lands in Document
 * Services with the rest, carrying the pinned coordinate and the district /
 * mandal / village resolved for it.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  notifyShortfallResult,
  payShortfall,
} from "../../utils/payShortfall";
import { formatRupees } from "../../utils/formatRupees";
import { apiMsg, msg } from "../../utils/messages";
import {
  MapPin,
  X,
  Loader2,
  FileText,
  Check,
  ArrowLeft,
  ArrowRight,
  Coins,
  Info,
} from "lucide-react";
import {
  fetchCrowdMode,
  fetchDocService,
  creditServiceOptions,
  createCrowdDataRequest,
  isHardCopyService,
  DOC_PRICE_FALLBACK,
} from "../../../pages/maps/crowdDataApi";
import { refreshCustomerCredits } from "../../utils/refreshCustomerCredits";
import { cadastralApi } from "../../utils/cadastralApi";
import TelanganaEcInstant from "../../../pages/statedocuments/components/TelanganaEcInstant";

// An EC can be fetched instantly from the pin, with the OTP sent to the
// customer's mobile: the survey parcel under it gives the village code and
// survey number for the portal's search by property. The backend picks the
// fetched EC up by its request id (crowddatarequest/instant.js → the document
// request consumer).
//  - Andhra Pradesh: by survey number alone.
//  - Telangana, non-agricultural only: the portal also needs a plot or door
//    number (a survey alone can match a thousand deeds), so the card asks for
//    one. An agricultural Telangana EC is Bhu Bharati — no OTP route; the team
//    sources it.
const AP = "andhra-pradesh";
const TS = "telangana";
export default function MapRequestCard({
  position,
  address,
  locationDetails,
  areaLabel,
  // Everything the host already knows. `state` is { key, name }; `mode` is
  // "agriculture" | "non-agriculture"; `fixedDocument` short-circuits the picker
  // when the visitor came from a document page.
  state,
  mode,
  fixedDocument = null,
  allowedDocuments = [],
  // False when the pin is outside the states we publish a catalogue for. There
  // is then no document list to offer, so the card asks for the location alone
  // and the team confirms what's available.
  stateServed = true,
  onClose,
  onSubmitted,
  onRepin,
  // Returns false and raises the login popup when there's no session — checked
  // before the document step, not at submit, so nobody picks a document and a
  // price only to be stopped at the end.
  requireLogin,
  // The state the PIN resolved to, when it disagrees with the state being
  // requested. Doesn't block — the host page's state wins — but silently
  // sourcing a Telangana record for a pin in Andhra Pradesh would be a bad
  // surprise, so it is said out loud.
  stateMismatch = null,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [step, setStep] = useState(fixedDocument ? "service" : "address");
  // A request that names only the place. Used where we serve no catalogue, or
  // where the catalogue turns up empty for this state + property type: better
  // to take the location and let the team come back than to dead-end the
  // visitor. The backend prices an unlisted document at its default.
  const LOCATION_ONLY = {
    id: "other",
    name: "Location request — document to be confirmed",
  };
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [doc, setDoc] = useState(fixedDocument);
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const stateId = state?.key || state?.id || null;

  // The survey parcel under the pin ({ state, sy, dmv, village, kind }), from
  // the same government cadastre as the map's survey layer. Shown on the card
  // and on the receipt; the backend looks it up again for itself and writes
  // the survey number onto the request.
  const [parcel, setParcel] = useState(null);
  useEffect(() => {
    setParcel(null);
    if (!position) return;
    const ctrl = new AbortController();
    cadastralApi
      .identify(position.lat, position.lng, { signal: ctrl.signal })
      .then((out) => {
        const p = out?.parcel?.properties;
        if (p && out.state) setParcel({ ...p, state: out.state });
      })
      .catch(() => {});
    return () => ctrl.abort();
    // Keyed on the coordinate values, not the object's identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position?.lat, position?.lng]);
  // Only when the pin is in the state being requested.
  const pinParcel = parcel && parcel.state === stateId ? parcel : null;
  const surveyLabel =
    pinParcel?.sy && (pinParcel.kind === "town" ? `T.S. No. ${pinParcel.sy}` : `Sy. No. ${pinParcel.sy}`);

  // Instant AP EC: the customer's mobile (prefilled from the profile), the OTP
  // modal, and — when the instant route can't be used (no documents found,
  // too many, an error) — a plain request the team sources instead.
  const customer = useSelector((st) => st.customer?.Info);
  const [ecMobile, setEcMobile] = useState("");
  useEffect(() => {
    const m = String(customer?.phone?.number || "").replace(/\D/g, "").slice(-10);
    if (m) setEcMobile((cur) => cur || m);
  }, [customer]);
  const [ecOpen, setEcOpen] = useState(false);
  const [ecManual, setEcManual] = useState(false);
  const ecDoneRef = useRef(false);
  const [ecPlot, setEcPlot] = useState("");
  const [ecDoor, setEcDoor] = useState("");
  // The property type from the host page or the map. When neither knows it,
  // the visitor is NOT asked: the request is placed without one at the base
  // price and the team confirms the record (it is never made instant).
  const effMode = mode || null;
  const ecTs = stateId === TS && effMode === "non-agriculture";
  const ecInstant =
    doc?.id === "ec" &&
    ((stateId === AP && !!effMode) || ecTs) &&
    !!pinParcel?.sy &&
    !!pinParcel?.dmv &&
    pinParcel.kind !== "town" &&
    !ecManual;
  // The documents this flow offers for the pinned state + property type, in the
  // order the backend config lists them.
  useEffect(() => {
    if (step !== "documents" || !stateId) return;
    let alive = true;
    setDocsLoading(true);
    // Without a property type the list is only for picking the document — every
    // id the map flow offers exists in both modes (public/crowd-data/index.json).
    fetchCrowdMode(stateId, effMode || "non-agriculture")
      .then((cfg) => {
        if (!alive) return;
        const all = cfg?.documents || [];
        const only =
          allowedDocuments.length > 0
            ? allowedDocuments.map((id) => all.find((d) => d.id === id)).filter(Boolean)
            : all;
        setDocs(only);
      })
      .catch(() => alive && setDocs([]))
      .finally(() => alive && setDocsLoading(false));
    return () => {
      alive = false;
    };
  }, [step, stateId, effMode, allowedDocuments]);

  // The credit-priced delivery options for the chosen document.
  useEffect(() => {
    if (step !== "service" || !doc?.id || !stateId || !effMode) return;
    let alive = true;
    setServicesLoading(true);
    setPicked(null);
    fetchDocService(stateId, effMode, doc.id)
      .then((cfg) => {
        if (!alive) return;
        // Year-banded options can't be resolved without a registration year,
        // which this flow never asks for — so only year-independent ones are
        // offered. A document with nothing left is still requestable at the
        // backend's default price.
        const opts = creditServiceOptions(cfg).filter((o) => !o.year);
        setServices(opts);
        if (opts.length === 1) setPicked(opts[0]);
      })
      .catch(() => alive && setServices([]))
      .finally(() => alive && setServicesLoading(false));
    return () => {
      alive = false;
    };
  }, [step, doc, stateId, effMode]);

  const price = picked?.credits ?? null;

  // Where a placed order goes. The customer Orders screen is
  // /customer/dashboard/overview — the same target the navbar's "My Orders"
  // uses; "/dashboard" is a different page and was the wrong link here.
  // Straight to the customer's orders. A plain route change, no router state:
  // the order just placed is the newest, so it is the top row when they land.
  const goToOrders = () => {
    onClose?.();
    navigate("/customer/dashboard/overview");
  };

  // Clear the receipt and the pin but leave the map up, so a second document for
  // a different spot doesn't mean reopening the whole flow.
  const placeAnother = () => {
    setDone(false);
    setDoc(fixedDocument);
    setPicked(null);
    setServices([]);
    setError("");
    setEcManual(false);
    setEcPlot("");
    setEcDoor("");
    setStep(fixedDocument ? "service" : "address");
    onRepin?.();
  };
  // What an unlisted document costs (the backend's default). Shown rather than
  // hidden: this path still charges.
  const fallbackPrice = DOC_PRICE_FALLBACK;

  const requestLocationOnly = () => {
    if (requireLogin && !requireLogin()) return;
    setDoc(LOCATION_ONLY);
    setPicked(null);
    setServices([]);
    setStep("service");
  };

  // "Request this document": an instant AP EC asks for the OTP first; the
  // request is placed once it is verified (or without it, when the OTP route
  // fails — the team then fetches the EC).
  const onRequest = () => {
    if (!ecInstant) return submit();
    if (!/^[6-9]\d{9}$/.test(ecMobile)) {
      setError(msg("EC.MOBILE_INVALID"));
      return;
    }
    if (ecTs && !ecPlot.trim() && !ecDoor.trim()) {
      setError(msg("EC.PLOT_OR_DOOR"));
      return;
    }
    setError("");
    ecDoneRef.current = false;
    setEcOpen(true);
  };

  const submit = async (extra = {}) => {
    if (submitting || !doc?.id) return;
    setSubmitting(true);
    setError("");
    try {
      const created = await createCrowdDataRequest({
        state: { id: stateId, name: state?.name || "" },
        // Omitted when unknown; the request is then priced at the base price.
        ...(effMode ? { landType: effMode } : {}),
        documentType: doc.id,
        documentTypeName: doc.name,
        ...(picked
          ? { serviceType: picked.serviceType, service: picked.service }
          : {}),
        ...(address ? { address } : {}),
        lat: position.lat,
        lng: position.lng,
        ...(locationDetails ? { locationDetails } : {}),
        ...extra,
      }, { payShortfall: true });
      const checkout = created?.data?.item;
      if (checkout?.sessionId) {
        // Credits were short: pay the rest; the request is created once paid.
        const status = await payShortfall(checkout);
        refreshCustomerCredits(dispatch);
        // notifyShortfallResult already toasts the failure; no inline repeat.
        if (!notifyShortfallResult({ status })) return;
      }
      refreshCustomerCredits(dispatch);
      setDone(true);
      onSubmitted?.();
    } catch (err) {
      const data = err?.response?.data;
      setError(apiMsg(data?.status || data, "ORDER.FAILED"));
    } finally {
      setSubmitting(false);
    }
  };

  // A hard copy needs a shipping address this card doesn't collect, so those
  // options are hidden rather than half-supported.
  const deliverable = useMemo(
    () => services.filter((o) => !isHardCopyService(o)),
    [services]
  );

  // Placing an order is the one moment in this flow worth celebrating, and the
  // one the visitor needs to trust — so it leaves the little map card and takes
  // the screen: what was ordered, where, what it cost, and the way to track it.
  if (done) {
    // `pointer-events-auto` below is load-bearing, not cosmetic: this popup
    // renders inside the map modal's `pointer-events-none` overlay wrapper (so
    // the small card doesn't block the map around it). Without resetting it the
    // receipt is VISIBLE but every click falls through to the Leaflet map
    // underneath — the buttons look dead, and clicking one drops a pin instead.
    return (
      <div className="pointer-events-auto fixed inset-0 z-[1400] flex items-center justify-center bg-[#0b1f3f]/60 p-4 backdrop-blur-sm">
        <style>{`
          @keyframes jgPop  { 0% { transform: scale(.82); opacity: 0 } 60% { transform: scale(1.04) } 100% { transform: scale(1); opacity: 1 } }
          @keyframes jgRing { 0% { transform: scale(.7); opacity: .55 } 100% { transform: scale(1.75); opacity: 0 } }
          @keyframes jgTick { to { stroke-dashoffset: 0 } }
          .jg-ok        { animation: jgPop .45s cubic-bezier(.22,1,.36,1) both }
          .jg-ok-ring   { animation: jgRing 1.6s ease-out infinite }
          .jg-ok-tick   { stroke-dasharray: 28; stroke-dashoffset: 28; animation: jgTick .5s .25s ease-out forwards }
          @media (prefers-reduced-motion: reduce) {
            .jg-ok, .jg-ok-ring, .jg-ok-tick { animation: none; opacity: 1; stroke-dashoffset: 0 }
          }
        `}</style>

        <div className="jg-ok w-full max-w-[380px] overflow-hidden rounded-2xl bg-white shadow-[0_40px_90px_-24px_rgba(11,31,63,0.7)]">
          {/* Navy cap with the tick, so the moment reads as a milestone. */}
          <div className="relative overflow-hidden bg-gradient-to-br from-[#002b5c] to-primary px-6 pb-6 pt-7 text-center">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -right-10 -top-14 h-40 w-40 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, rgba(28,75,147,0.25) 0%, transparent 70%)",
              }}
            />
            <span className="relative mx-auto grid h-16 w-16 place-items-center">
              <span className="jg-ok-ring absolute inset-0 rounded-full bg-green/40" />
              <span className="relative grid h-16 w-16 place-items-center rounded-full bg-green shadow-[0_10px_28px_-8px_rgba(95,213,104,0.9)]">
                <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none">
                  <path
                    className="jg-ok-tick"
                    d="M5 12.5l4.5 4.5L19 7.5"
                    stroke="#fff"
                    strokeWidth="2.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
            <h3 className="relative mt-4 text-[19px] font-bold leading-tight text-white">
              {msg("ORDER.PLACED")}
            </h3>
            <p className="relative mt-1 text-[12.5px] font-medium text-white/70">
              We&apos;ve started sourcing it for you
            </p>
          </div>

          {/* Receipt: what, where, how much. */}
          <div className="divide-y divide-slate-100 px-5 py-1">
            <Row label="Document" value={doc?.name} />
            <Row label="Location" value={areaLabel || address} />
            {surveyLabel && <Row label="Survey No" value={surveyLabel} />}
            <Row
              label="Coordinates"
              value={`${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}`}
            />
            <Row
              label="Paid"
              value={
                <span className="inline-flex items-center gap-1 font-bold text-primary">
                  {isFree(price ?? fallbackPrice) ? (
                    "Free"
                  ) : (
                    formatRupees(price ?? fallbackPrice)
                  )}
                </span>
              }
            />
          </div>

          {/* One way on: the order is placed, so the useful next screen is the
              order itself. Closes the map first — leaving a full-screen map
              mounted behind a route change strands its Leaflet instance. */}
          <div className="flex gap-2 px-5 pb-5 pt-4">
            <button
              type="button"
              onClick={placeAnother}
              className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-[13px] font-bold text-grey transition-colors hover:bg-slate-50 hover:text-primary"
            >
              Place another
            </button>
            <button
              type="button"
              onClick={goToOrders}
              className="flex flex-[1.4] items-center justify-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-[13px] font-bold text-white shadow-[0_10px_26px_-12px_rgba(21,53,104,0.9)] transition-all hover:bg-dark"
            >
              View my orders
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Shell onClose={onClose}>
      {/* What was pinned. The PLACE is the hero — it is what the visitor
          recognises — with state / property type as quiet supporting facts.
          The coordinate is deliberately absent: the map already prints it in its
          own readout directly beside this card, and showing it twice just adds
          noise to the one panel that should read cleanly. */}
      <div className="px-4 pt-4 sm:px-5">
        <p className="flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-[#8493b4]">
          <MapPin className="h-3 w-3 text-gold" />
          Near this address
        </p>
        <p className="mt-1.5 text-[14px] font-bold leading-[1.3] text-primary">
          {areaLabel || address || "Pinned location"}
        </p>
        {state?.name && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="rounded-full bg-primary/[0.07] px-2 py-0.5 text-[10.5px] font-bold text-primary">
              {state.name}
            </span>
            {effMode && (
              <span className="rounded-full bg-gold/[0.14] px-2 py-0.5 text-[10.5px] font-bold text-[#9a6b00]">
                {effMode === "agriculture" ? "Agricultural" : "Non-agricultural"}
              </span>
            )}
            {surveyLabel && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-bold text-grey">
                {surveyLabel}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Hairline between "where" and "what" — the card's two halves. */}
      <div className="mx-4 mt-3.5 h-px bg-slate-200/80 sm:mx-5" />

      <div className="px-4 pb-4 pt-3.5 sm:px-5">
        {stateMismatch && (
          <p className="mb-2.5 rounded-lg bg-[#f2f6fb] px-3 py-2 text-[11px] font-semibold leading-snug text-[#9a6b00]">
            This pin looks like it&apos;s in {stateMismatch}, but you&apos;re
            requesting a {state?.name} record.
          </p>
        )}

        {step === "address" && (
          <>
            {stateServed ? (
              <button
                type="button"
                onClick={() => {
                  if (requireLogin && !requireLogin()) return;
                  setStep("documents");
                }}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-[13px] font-bold text-white shadow-[0_10px_26px_-12px_rgba(21,53,104,0.9)] transition-all hover:bg-dark"
              >
                <FileText className="h-4 w-4" />
                Get documents for this location
              </button>
            ) : (
              <>
                <p className="mb-2 rounded-lg bg-slate-50 px-3 py-2 text-[11.5px] font-medium leading-snug text-grey">
                  We don&apos;t list documents for{" "}
                  {state?.name || "this state"} yet — send us the location and
                  our team will confirm what&apos;s available.
                </p>
                <button
                  type="button"
                  onClick={requestLocationOnly}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-[13px] font-bold text-white shadow-[0_10px_26px_-12px_rgba(21,53,104,0.9)] transition-all hover:bg-dark"
                >
                  <MapPin className="h-4 w-4" />
                  Request this location
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onRepin}
              className="mt-2 w-full rounded-xl px-4 py-2 text-[12px] font-semibold text-grey transition-colors hover:bg-slate-100 hover:text-primary"
            >
              Pick a different spot
            </button>
          </>
        )}

        {step === "documents" && (
          <>
            <Label
              onBack={() => setStep("address")}
            >
              Which document do you need?
            </Label>
            {docsLoading ? (
              <Waiting>Loading documents…</Waiting>
            ) : docs.length === 0 ? (
              <>
                <p className="mb-2 rounded-lg bg-slate-50 px-3 py-2 text-[11.5px] font-medium leading-snug text-grey">
                  No documents are listed for this location and property type.
                  Send the location instead and our team will confirm
                  what&apos;s available.
                </p>
                <button
                  type="button"
                  onClick={requestLocationOnly}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-[13px] font-bold text-white shadow-[0_10px_26px_-12px_rgba(21,53,104,0.9)] transition-all hover:bg-dark"
                >
                  <MapPin className="h-4 w-4" />
                  Request this location
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-1.5">
                {docs.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => {
                      setDoc(d);
                      setStep("service");
                    }}
                    className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-left text-[12.5px] font-semibold text-primary transition-all hover:border-primary hover:bg-primary/5"
                  >
                    <span className="min-w-0 truncate">{d.name}</span>
                    <FileText className="h-3.5 w-3.5 shrink-0 text-[#8493b4]" />
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {step === "service" && (
          <>
            <Label
              onBack={
                fixedDocument ? () => setStep("address") : () => setStep("documents")
              }
            >
              {doc?.name}
            </Label>

            {servicesLoading ? (
              <Waiting>Loading options…</Waiting>
            ) : deliverable.length === 0 ? (
              <div className="mb-3 rounded-lg border border-[#1c4b93]/30 bg-[#f2f6fb] px-3 py-2.5">
                {/* No listed price for this one, so the charge is a STARTING
                    amount, not the final bill: an admin can raise a payment link
                    on the request for the balance once they know what sourcing
                    it takes (crowddatarequest/payment.controller.js). "Base
                    price" is therefore the accurate word — quoting it as the
                    price would be a promise the flow can't keep. */}
                <p className="flex items-center gap-1.5 text-[11px] font-bold text-[#9a6b00]">
                  <Info className="h-3.5 w-3.5 shrink-0" />
                  Base price
                </p>
                <p className="mt-1 text-[11.5px] font-medium leading-snug text-grey">
                  You&apos;ll be charged{" "}
                  <span className="font-bold text-primary">
                    {formatRupees(fallbackPrice)}
                  </span>{" "}
                  to start. Our team will check what&apos;s available for this
                  location and contact you — the final price may change
                  depending on the document.
                </p>
              </div>
            ) : (
              <div className="mb-3 flex flex-col gap-1.5">
                {deliverable.map((o) => {
                  const on = picked?.service?.id === o.service.id;
                  return (
                    <button
                      key={`${o.serviceType.id}-${o.service.id}`}
                      type="button"
                      onClick={() => setPicked(o)}
                      className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left transition-all ${
                        on
                          ? "border-primary bg-primary/[0.055] ring-1 ring-primary/30"
                          : "border-slate-200 hover:border-primary/50 hover:bg-slate-50"
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5 text-[12.5px] font-bold text-primary">
                          {on && (
                            <Check
                              className="h-3.5 w-3.5 shrink-0 text-primary"
                              strokeWidth={3}
                            />
                          )}
                          <span className="truncate">{o.service.name}</span>
                        </span>
                        {o.desc && (
                          <span className="mt-0.5 block truncate text-[10.5px] font-medium text-grey">
                            {o.desc}
                          </span>
                        )}
                      </span>
                      <span
                        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-gold/[0.14] px-2 py-0.5 text-[11px] font-bold text-[#9a6b00]"
                      >
                        {isFree(o.credits) ? (
                          "Free"
                        ) : (
                          formatRupees(o.credits)
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {ecInstant && (
              <div className="mb-3">
                <label className="mb-1 block text-[11px] font-bold text-grey" htmlFor="map-ec-mobile">
                  Mobile number for the EC OTP
                </label>
                <input
                  id="map-ec-mobile"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={ecMobile}
                  onChange={(e) => setEcMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[13px] font-semibold text-primary outline-none focus:border-primary"
                  placeholder="10-digit mobile"
                />
                {ecTs && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      maxLength={30}
                      value={ecPlot}
                      onChange={(e) => setEcPlot(e.target.value)}
                      aria-label="Plot number"
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[13px] font-semibold text-primary outline-none focus:border-primary"
                      placeholder="Plot no"
                    />
                    <input
                      type="text"
                      maxLength={30}
                      value={ecDoor}
                      onChange={(e) => setEcDoor(e.target.value)}
                      aria-label="Door number"
                      className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[13px] font-semibold text-primary outline-none focus:border-primary"
                      placeholder="or Door no"
                    />
                  </div>
                )}
                <p className="mt-1 text-[10.5px] font-medium leading-snug text-grey">
                  Instant EC for {surveyLabel}. The government portal sends an OTP to this number.
                  {ecTs && " Plot or door number as written in your deed."}
                </p>
              </div>
            )}
            {ecManual && doc?.id === "ec" && (
              <p className="mb-2 rounded-lg bg-[#f2f6fb] px-3 py-2 text-[11.5px] font-medium leading-snug text-grey">
                We couldn&apos;t fetch this EC instantly. You can still request it —
                our team will source it for {surveyLabel || "this location"}.
              </p>
            )}

            {error && (
              <p className="mb-2 rounded-lg bg-[#FDECEC] px-3 py-2 text-[11.5px] font-semibold leading-snug text-[#c0392b]">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={onRequest}
              disabled={submitting || (deliverable.length > 0 && !picked)}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-[13px] font-bold text-white shadow-[0_10px_26px_-12px_rgba(21,53,104,0.9)] transition-all hover:bg-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Placing request…
                </>
              ) : (
                <>
                  {doc?.id === "other" ? "Request this location" : "Request this document"}
                  {(price ?? fallbackPrice) != null && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-bold">
                      {isFree(price ?? fallbackPrice) ? (
                        "Free"
                      ) : (
                        formatRupees(price ?? fallbackPrice)
                      )}
                    </span>
                  )}
                </>
              )}
            </button>
          </>
        )}
      </div>

      {ecOpen && (
        <TelanganaEcInstant
          open={ecOpen}
          stateId={stateId}
          mobile={ecMobile}
          property={{
            // TS: district / mandal are looked up from the village code
            // server-side (tsEcInstant/service.js startProperty).
            villageCode: pinParcel?.dmv,
            villageName: pinParcel?.village,
            surveyNo: pinParcel?.sy,
            ...(ecTs ? { plotNo: ecPlot.trim(), houseNo: ecDoor.trim() } : {}),
          }}
          onEcReady={(requestId) => {
            ecDoneRef.current = true;
            setEcOpen(false);
            submit({ otpRequestId: requestId });
          }}
          onPlaceWithoutEc={(reason) => {
            ecDoneRef.current = true;
            setEcOpen(false);
            submit({ ecWithoutOtp: reason });
          }}
          onClose={() => {
            setEcOpen(false);
            // Closed without an EC (no documents, too many, an error):
            // the next click places a plain request for the team.
            if (!ecDoneRef.current) setEcManual(true);
          }}
        />
      )}
    </Shell>
  );
}

// One line of the order receipt. Hidden when there is nothing to show, so a
// request with no resolved address doesn't leave a blank row.
// A 0-credit option is shown as "Free" rather than "0 credits".
function isFree(credits) {
  return credits != null && Number(credits) === 0;
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-3 py-2.5">
      <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-[#8493b4]">
        {label}
      </span>
      <span className="min-w-0 text-right text-[12.5px] font-semibold leading-snug text-primary">
        {value}
      </span>
    </div>
  );
}

function Shell({ children, onClose }) {
  return (
    <div className="pointer-events-auto relative w-[min(92vw,360px)] overflow-hidden rounded-2xl bg-white shadow-[0_2px_6px_rgba(11,31,63,0.10),0_28px_70px_-20px_rgba(11,31,63,0.65)] ring-1 ring-black/[0.06]">
      {/* Gold hairline along the top edge: ties the panel to the brand and gives
          it a defined lip against bright satellite imagery, where a plain white
          rectangle otherwise dissolves into the terrain. */}
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-gold via-orange to-gold"
      />
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-2 top-2 z-10 grid h-7 w-7 place-items-center rounded-lg text-[#8493b4] transition-colors hover:bg-slate-100 hover:text-primary"
      >
        <X className="h-4 w-4" />
      </button>
      {children}
    </div>
  );
}

function Label({ children, onBack }) {
  return (
    <div className="mb-2 flex items-center gap-1.5">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-[#8493b4] transition-colors hover:bg-slate-100 hover:text-primary"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
        </button>
      )}
      <p className="min-w-0 truncate text-[12px] font-bold text-primary">
        {children}
      </p>
    </div>
  );
}

function Waiting({ children }) {
  return (
    <p className="flex items-center justify-center gap-2 py-4 text-[12px] font-semibold text-grey">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      {children}
    </p>
  );
}
